/**
 * The optimistic summary bubble (contract C12, index amendment A4) —
 * byte-identical to the server's `render_summary`, because the persisted user
 * row replaces this one on refetch, in the same format.
 *
 * - Each answered question renders `«{header}: {value}»`, questions joined with
 *   `" · "` in round order; unanswered questions are omitted.
 * - `value`: «Не знаю» is exclusive — the word in `chat.clarify.summaryDontKnow`
 *   (RU `не знаю`, KY `билбейм`, EN `not sure`); otherwise the chosen labels in
 *   the question's option order, then the stripped «Свой ответ» text, joined
 *   with `", "`.
 * - A skip, or nothing answered, is the whole `chat.clarify.skipAll` label.
 *
 * The words come from the `chat` bundle for an EXPLICIT language
 * (`getFixedT`, as `lib/deep-links.ts` does) — the conversation's language,
 * not whatever the UI happens to show at render time.
 */
import { registerChatI18n } from "@/features/chat/chat-store";
import i18n, { type SupportedLanguage } from "@/lib/i18n";

import type { ClarifyAnswerInput, ClarifyRoundEvent } from "./types";

export function buildClarifySummary(
  round: ClarifyRoundEvent,
  answers: readonly ClarifyAnswerInput[],
  options: { skip: boolean; language: SupportedLanguage },
): string {
  registerChatI18n();
  const t = i18n.getFixedT(options.language, "chat");
  const skipLabel = t("clarify.skipAll");
  if (options.skip) return skipLabel;
  const parts: string[] = [];
  for (const question of round.questions) {
    const answer = answers.find((candidate) => candidate.q === question.id);
    if (answer === undefined) continue;
    let value: string;
    if (answer.dont_know === true) {
      value = t("clarify.summaryDontKnow");
    } else {
      const chosen = new Set(answer.opts);
      const labels = question.options
        .filter((option) => chosen.has(option.id))
        .map((option) => option.label);
      const custom = answer.custom?.trim() ?? "";
      value = (custom.length > 0 ? [...labels, custom] : labels).join(", ");
    }
    if (value.length === 0) continue;
    parts.push(`${question.header}: ${value}`);
  }
  return parts.length > 0 ? parts.join(" · ") : skipLabel;
}
